/**
 * LINKEDIN POST MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Come Note, LinkedinPost non ha enum: i mapper copiano i campi audit e i due
 * campi business (`activityDate`, `message`). Mantenuti come funzioni esplicite
 * per coerenza con il pattern del progetto.
 */

import { pickAuditFields } from '@crm/shared';
import type {
  LinkedinPost,
  LinkedinPostCreatePayload,
  LinkedinPostUpdatePayload,
} from './linkedinPostTypes';

/**
 * Shape del LinkedinPost come esce dal REST
 * (/o/crm/v1_0/linkedin-post/{linkedinPostId}). Rispecchia lo schema
 * LinkedinPost nel rest-openapi.yaml.
 */
export interface LinkedinPostDto {
  linkedinPostId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  activityDate: string;
  message: string;
}

/** DTO -> modello UI. */
export function linkedinPostFromDto(dto: LinkedinPostDto): LinkedinPost {
  return {
    linkedinPostId: dto.linkedinPostId,
    ...pickAuditFields(dto),
    activityDate: dto.activityDate,
    message: dto.message,
  };
}

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare la shape del
 * backend; non invocato dall'app reale.
 */
export function linkedinPostToDto(l: LinkedinPost): LinkedinPostDto {
  return {
    linkedinPostId: l.linkedinPostId,
    ...pickAuditFields(l),
    activityDate: l.activityDate,
    message: l.message,
  };
}

/**
 * Shape del payload accettato dal backend in POST/PATCH. Campi audit e
 * linkedinPostId non sono accettati qui (gestiti dal backend).
 */
export interface LinkedinPostInput {
  activityDate?: string;
  message?: string;
}

/** Payload create UI -> DTO input. */
export function linkedinPostCreateToInput(p: LinkedinPostCreatePayload): LinkedinPostInput {
  return {
    activityDate: p.activityDate,
    message: p.message,
  };
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function linkedinPostUpdateToInput(
  p: Omit<LinkedinPostUpdatePayload, 'linkedinPostId'>,
): LinkedinPostInput {
  const out: LinkedinPostInput = {};
  if (p.activityDate !== undefined) out.activityDate = p.activityDate;
  if (p.message !== undefined) out.message = p.message;
  return out;
}

/**
 * DTO input (PATCH body) -> Partial<LinkedinPost> UI. Usato dai mock MSW per
 * applicare il patch al mockLinkedinPosts storage (shape UI).
 */
export function linkedinPostInputFromDto(input: LinkedinPostInput): Partial<LinkedinPost> {
  const out: Partial<LinkedinPost> = {};
  if (input.activityDate !== undefined) out.activityDate = input.activityDate;
  if (input.message !== undefined) out.message = input.message;
  return out;
}
